import React from 'react';
import { COLORS } from '../utils/constants';

interface ContextMenuItem {
  label: string;
  action: () => void;
  dangerous?: boolean;
  /** When false, the menu stays open after the action (e.g. to open a submenu). Defaults to true. */
  closeOnClick?: boolean;
}

interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, items, onClose }) => {
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Adjust position to keep menu on screen
  const adjustedX = Math.min(x, window.innerWidth - 200);
  const adjustedY = Math.min(y, window.innerHeight - items.length * 32 - 20);

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: adjustedX,
        top: adjustedY,
        backgroundColor: 'white',
        border: '1px solid #ddd',
        borderRadius: '4px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 10000,
        minWidth: '160px',
        padding: '4px 0'
      }}
    >
      {items.map((item, index) => (
        <div
          key={index}
          style={{
            padding: '8px 12px',
            cursor: 'pointer',
            fontSize: '14px',
            color: item.dangerous ? COLORS.DANGER : '#2d3436',
            borderBottom: index === items.length - 1 ? 'none' : '1px solid #f0f0f0'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = item.dangerous ? '#fff5f5' : '#f8f9fa';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
          onClick={() => {
            item.action();
            if (item.closeOnClick !== false) {
              onClose();
            }
          }}
        >
          {item.label}
        </div>
      ))}
    </div>
  );
};